import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../auth/AuthContext';
import { getDisplayName, getDisplayNameForUI, getDisplayRole } from '../../../utils/displayName';
import { AVATAR_PLACEHOLDER } from '../../../utils/placeholderImage';
import { useNavigate, useLocation } from 'react-router-dom';
import { useJob } from '../../../contexts/JobContext';
import { analyticsService, orderService } from '../../../services/apiService';
import AdminLocationDropdown from './components/AdminLocationDropdown';
import { toast } from 'react-toastify';
import Sidebar from './Sidebar';
import Overview from './pages/Overview';
import Users from './pages/Users';
import Orders from './pages/Orders';
import Vendors from './pages/Vendors';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Products from './pages/Products';
import Categories from './pages/Categories';
import LuckyDraw from './pages/LuckyDraw';
import KYC from './pages/KYC';
import PartnerApplicationsPanel from './pages/PartnerApplicationsPanel';
import Fines from './pages/Fines';
import Reports from './pages/Reports';
import Garages from './pages/Garages';
import JobCardsQC from './pages/JobCardsQC';
import VehicleManagement from './pages/VehicleManagement';
import CatalogGapsPanel from './pages/CatalogGapsPanel';
import SellerReviewQueuePanel from './pages/SellerReviewQueuePanel';
import CatalogApprovalsPanel from './pages/CatalogApprovalsPanel';
import Profile from './pages/Profile';
import Payments from './pages/Payments';
import Invoices from './pages/Invoices';
import Marketing from './pages/Marketing';
import Deals from './pages/Deals';
import AvailabilityRequestsPanel from '../shared/AvailabilityRequestsPanel';
import DealAuditLogs from './pages/DealAuditLogs';
import ComboFailureDashboard from './pages/ComboFailureDashboard';
import Support from './pages/Support';
import DisputeArbitrationPanel from './pages/DisputeArbitrationPanel';
import FraudDetection from './pages/FraudDetection';
import RiskDashboard from './pages/RiskDashboard';
import CommsRules from './pages/CommsRules';
import Commissions from './pages/Commissions';
import Mechanics from './pages/Mechanics';
import Shipping from './pages/Shipping';
import {
  FaUsers,
  FaStore,
  FaTools,
  FaShoppingCart,
  FaChartLine,
  FaCog,
  FaSignOutAlt,
  FaHome,
  FaBox,
  FaMoneyBillWave,
  FaUserShield,
  FaBell,
  FaArrowUp,
  FaArrowDown,
  FaFilter,
  FaDownload,
  FaEye,
  FaEdit,
  FaTrash,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaChartBar,
  FaChartPie,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhone,
  FaBars,
  FaUser,
  FaChevronDown,
  FaSignInAlt,
  FaTruck
} from 'react-icons/fa';

const SuperAdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { jobs } = useJob();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dateRange, setDateRange] = useState('30d');
  const [selectedLocation, setSelectedLocation] = useState(() => {
    try {
      const raw = localStorage.getItem('adminLocation');
      if (!raw) return '';
      try { return JSON.parse(raw); } catch (e) { return raw; }
    } catch (e) {
      return '';
    }
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [overallStats, setOverallStats] = useState(null);
  const [previousStats, setPreviousStats] = useState(null);
  const [realTimeData, setRealTimeData] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [systemStatus, setSystemStatus] = useState({
    apiStatus: 'Unknown',
    database: 'Unknown',
    serverLoad: 0,
    uptime: '0%'
  });
  const [topVendorsData, setTopVendorsData] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderStats, setOrderStats] = useState(null);

  const fetchOrderStats = async () => {
    try {
      const stats = await orderService.getOrderStats();
      setOrderStats(stats);
    } catch (e) {
      setOrderStats(null);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchAdditionalData();
    fetchOrders();
    fetchOrderStats();
    const interval = setInterval(() => {
      fetchDashboardData();
      fetchAdditionalData();
      fetchOrders();
      fetchOrderStats();
    }, 30000);
    return () => clearInterval(interval);
  }, [dateRange, selectedLocation]);

  const fetchOrders = async () => {
    try {
      const filters = {};
      if (selectedLocation) {
        if (typeof selectedLocation === 'object') {
          if (selectedLocation.city) filters.city = selectedLocation.city;
          if (selectedLocation.state) filters.state = selectedLocation.state;
          if (selectedLocation.country) filters.country = selectedLocation.country;
          if (selectedLocation.area) filters.area = selectedLocation.area;
        } else {
          filters.location = selectedLocation;
        }
      }
      const response = await orderService.getOrders(filters);
      // Handle different response structures
      const ordersList = Array.isArray(response) ? response : 
                        (response.orders && Array.isArray(response.orders)) ? response.orders : [];
      
      const mappedOrders = ordersList.map(order => ({
        id: order._id || order.id,
        orderId: order.orderId || (order._id ? `ORD-${order._id.substring(0, 6).toUpperCase()}` : 'N/A'),
        customer: order.user?.name || order.customerName || 'Unknown Customer',
        vendor: order.items?.[0]?.vendor?.name || order.vendor?.name || order.vendorName || 'Unknown Vendor',
        amount: order.totalAmount ? `₹${order.totalAmount.toLocaleString()}` : (typeof order.amount === 'string' ? order.amount : `₹${order.amount || 0}`),
        status: order.status || 'Pending',
        date: order.createdAt ? new Date(order.createdAt).toLocaleDateString() : (order.date || new Date().toLocaleDateString()),
        items: order.items?.length || order.itemCount || 0
      }));
      
      setOrders(mappedOrders);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch current period stats (includes changes from backend)
      const overallFilters = { dateRange };
      if (selectedLocation) {
        if (typeof selectedLocation === 'object') {
          overallFilters.city = selectedLocation.city;
          overallFilters.state = selectedLocation.state;
          overallFilters.country = selectedLocation.country;
          if (selectedLocation.area) overallFilters.area = selectedLocation.area;
        } else {
          overallFilters.location = selectedLocation;
        }
      }
      const currentStats = await analyticsService.getOverallStats(overallFilters);
      setOverallStats(currentStats);
      
      // Fetch real-time dashboard data
      const dashboardData = await analyticsService.getRealTimeDashboard(overallFilters);
      setRealTimeData(dashboardData);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdditionalData = async () => {
    try {
      // Fetch recent activities, system status, and top vendors in parallel
      const filters = { limit: 6, dateRange };
      if (selectedLocation) {
        if (typeof selectedLocation === 'object') {
          if (selectedLocation.city) filters.city = selectedLocation.city;
          if (selectedLocation.state) filters.state = selectedLocation.state;
          if (selectedLocation.country) filters.country = selectedLocation.country;
          if (selectedLocation.area) filters.area = selectedLocation.area;
        } else {
          filters.location = selectedLocation;
        }
      }
      const [activities, health, vendors] = await Promise.all([
        analyticsService.getRecentActivities(filters),
        analyticsService.getSystemHealth(),
        analyticsService.getTopVendors({ dateRange, limit: 5, ...(selectedLocation ? (typeof selectedLocation === 'object' ? { city: selectedLocation.city, state: selectedLocation.state, country: selectedLocation.country } : { location: selectedLocation }) : {}) })
      ]);

      setRecentActivities(Array.isArray(activities) ? activities : []);
      setSystemStatus(health || {});
      setTopVendorsData(Array.isArray(vendors) ? vendors : []);
    } catch (error) {
      console.error('Failed to fetch additional data:', error);
      // Don't show toast for these as they're background updates
    }
  };

  // Generate time series data from orders (if we have order data)
  // For now, we'll generate placeholder data but structure it for real data
  const generateTimeSeriesData = (days = 30) => {
    const data = [];
    const today = new Date();
    const totalRevenue = overallStats?.totalRevenue || 0;
    const totalOrders = overallStats?.totalOrders || 0;
    
    // Distribute total revenue and orders across days
    const avgDailyRevenue = totalRevenue / days;
    const avgDailyOrders = totalOrders / days;
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      // Add some variation to make it look realistic
      const variation = 0.8 + Math.random() * 0.4; // 80% to 120% of average
      data.push({
        date: date.toISOString().split('T')[0],
        orders: Math.round(avgDailyOrders * variation),
        revenue: Math.round(avgDailyRevenue * variation),
        users: Math.floor(Math.random() * 10) + 5,
      });
    }
    return data;
  };

  const timeSeriesData = useMemo(() => generateTimeSeriesData(
    dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90
  ), [dateRange, overallStats, refreshKey]);

  const stats = useMemo(() => {
    // Use real data from backend if available, otherwise use defaults
    const totalUsers = overallStats?.totalUsers ?? 0; // new customers in period (for changes)
    const totalUsersAll = overallStats?.totalUsersAll ?? totalUsers ?? 0; // total users in system for card value
    const totalVendors = overallStats?.totalVendors || 0;
    const totalMechanics = realTimeData?.onlineMechanics || 0;
    const totalOrders = overallStats?.totalOrders || 0;
    const totalRevenue = overallStats?.totalRevenue || 0;
    const totalProducts = overallStats?.totalProducts || 0;
    const activeJobs = realTimeData?.activeJobs || 0;
    const completedJobs = overallStats?.completedJobs || 0;
    const totalGarages = overallStats?.totalGarages || 0;

    // Use changes from backend if available
    const changes = overallStats?.changes || {
      users: 0,
      vendors: 0,
      mechanics: 0,
      orders: 0,
      revenue: 0,
      products: 0,
    };

    const calculated = {
      totalUsers,
      totalUsersAll,
      totalVendors,
      totalMechanics,
      totalOrders,
      totalRevenue,
      totalProducts,
      activeJobs,
      completedJobs,
      totalGarages,
      changes,
    };
    return [
      {
        label: 'Total Users',
        value: calculated.totalUsersAll.toLocaleString(),
        icon: FaUsers,
        color: 'bg-blue-500',
        change: `${calculated.changes.users >= 0 ? '+' : ''}${calculated.changes.users}%`,
        trend: calculated.changes.users > 0 ? 'up' : calculated.changes.users < 0 ? 'down' : 'neutral',
        path: '/admin/dashboard/users'
      },
      {
        label: 'Vendors',
        value: calculated.totalVendors.toLocaleString(),
        icon: FaStore,
        color: 'bg-green-500',
        change: `${calculated.changes.vendors >= 0 ? '+' : ''}${calculated.changes.vendors}%`,
        trend: calculated.changes.vendors > 0 ? 'up' : calculated.changes.vendors < 0 ? 'down' : 'neutral',
        path: '/admin/dashboard/vendors'
      },
      {
        label: 'Mechanics',
        value: calculated.totalMechanics.toLocaleString(),
        icon: FaTools,
        color: 'bg-yellow-500',
        change: `${calculated.activeJobs} ongoing`,
        trend: 'neutral',
        path: '/admin/dashboard/mechanics'
      },
      {
        label: 'Total Orders',
        value: calculated.totalOrders.toLocaleString(),
        icon: FaShoppingCart,
        color: 'bg-purple-500',
        change: `${calculated.changes.orders >= 0 ? '+' : ''}${calculated.changes.orders}%`,
        trend: calculated.changes.orders > 0 ? 'up' : calculated.changes.orders < 0 ? 'down' : 'neutral',
        path: '/admin/dashboard/orders'
      },
      {
        label: 'Revenue',
        value: `₹${(calculated.totalRevenue / 1000000).toFixed(2)}M`,
        icon: FaMoneyBillWave,
        color: 'bg-red-500',
        change: `${calculated.changes.revenue >= 0 ? '+' : ''}${calculated.changes.revenue}%`,
        trend: calculated.changes.revenue > 0 ? 'up' : calculated.changes.revenue < 0 ? 'down' : 'neutral',
        path: '/admin/dashboard/analytics'
      },
      {
        label: 'Products',
        value: calculated.totalProducts.toLocaleString(),
        icon: FaBox,
        color: 'bg-indigo-500',
        change: `${calculated.changes.products >= 0 ? '+' : ''}${calculated.changes.products}%`,
        trend: calculated.changes.products > 0 ? 'up' : calculated.changes.products < 0 ? 'down' : 'neutral',
        path: '/admin/dashboard/products'
      },
      {
        label: 'Active Jobs',
        value: calculated.activeJobs.toString(),
        icon: FaTools,
        color: 'bg-orange-500',
        change: `${calculated.activeJobs} ongoing`,
        trend: 'neutral',
        path: '/admin/dashboard/orders'
      },
      {
        label: 'Total Garages',
        value: calculated.totalGarages.toLocaleString(),
        icon: FaTools,
        color: 'bg-teal-500',
        change: `${calculated.changes.garages >= 0 ? '+' : ''}${calculated.changes.garages || 0}%`,
        trend: calculated.changes.garages > 0 ? 'up' : calculated.changes.garages < 0 ? 'down' : 'neutral',
        path: '/admin/dashboard/garages'
      },
      {
        label: 'Cancelled Orders',
        value: (orderStats?.cancelledOrders ?? 0).toLocaleString(),
        icon: FaTimesCircle,
        color: 'bg-red-500',
        change: 'View all',
        trend: 'neutral',
        path: '/admin/dashboard/orders?status=cancelled'
      },
    ];
  }, [overallStats, realTimeData, jobs, orderStats]);

  // Helper function to get icon for activity type
  const getActivityIcon = (type) => {
    switch (type) {
      case 'user':
        return FaUsers;
      case 'order':
        return FaShoppingCart;
      case 'product':
        return FaBox;
      default:
        return FaCheckCircle;
    }
  };

  // User management data
  const usersData = useMemo(() => {
    return [
      { id: 1, name: 'John Doe', email: 'john@example.com', role: 'Customer', status: 'Active', joined: '2024-01-15', orders: 12 },
      { id: 2, name: 'Jane Smith', email: 'jane@example.com', role: 'Vendor', status: 'Active', joined: '2024-01-10', orders: 45 },
      { id: 3, name: 'Bob Johnson', email: 'bob@example.com', role: 'Mechanic', status: 'Active', joined: '2024-01-08', orders: 8 },
      { id: 4, name: 'Alice Brown', email: 'alice@example.com', role: 'Customer', status: 'Inactive', joined: '2023-12-20', orders: 3 },
      { id: 5, name: 'Charlie Wilson', email: 'charlie@example.com', role: 'Vendor', status: 'Active', joined: '2024-01-05', orders: 67 },
    ];
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showProfileMenu && !event.target.closest('.profile-menu')) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileMenu]);

  // Determine which page to show based on location
  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/admin/dashboard' || path === '/admin/dashboard/') return 'overview';
    if (path.includes('/profile')) return 'profile';
    if (path.includes('/users')) return 'users';
    if (path.includes('/orders')) return 'orders';
    if (path.includes('/availability')) return 'availability';
    if (path.includes('/vendors')) return 'vendors';
    if (path.includes('/mechanics')) return 'mechanics';
    if (path.includes('/garages')) return 'garages';
    if (path.includes('/vehicles')) return 'vehicles';
    if (path.includes('/catalog-gaps')) return 'catalog-gaps';
    if (path.includes('/seller-review-queue')) return 'seller-review-queue';
    if (path.includes('/catalog-approvals')) return 'catalog-approvals';
    if (path.includes('/jobcards-qc')) return 'jobcards-qc';
    if (path.includes('/shipping')) return 'shipping';
    if (path.includes('/products')) return 'products';
    if (path.includes('/categories')) return 'categories';
    if (path.includes('/lucky-draw')) return 'lucky-draw';
    if (path.includes('/kyc')) return 'kyc';
    if (path.includes('/partner-applications')) return 'partner-applications';
    if (path.includes('/fines')) return 'fines';
    if (path.includes('/analytics')) return 'analytics';
    if (path.includes('/reports')) return 'reports';
    if (path.includes('/settings')) return 'settings';
    if (path.includes('/payments')) return 'payments';
    if (path.includes('/invoices')) return 'invoices';
    if (path.includes('/marketing')) return 'marketing';
    if (path.includes('/combo-failures')) return 'combo-failures';
    if (path.includes('/deal-audit')) return 'deal-audit';
    if (path.includes('/deals')) return 'deals';
    if (path.includes('/support')) return 'support';
    if (path.includes('/fraud')) return 'fraud';
    if (path.includes('/risk')) return 'risk';
    if (path.includes('/disputes')) return 'disputes';
    if (path.includes('/comms-rules')) return 'comms-rules';
    if (path.includes('/commissions')) return 'commissions';
    return 'overview';
  };

  const currentPage = getCurrentPage();

  return (
    <div className="sp-dashboard-premium sp-dashboard-dark admin-dashboard-light h-screen min-h-0 flex overflow-hidden bg-white">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="dashboard-header shrink-0">
          <div className="px-4 md:px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="lg:hidden p-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition"
                  aria-label="Toggle menu"
                >
                  <FaBars className="text-xl" />
                </button>
                <div>
                  <h1 className="text-lg md:text-xl font-bold text-slate-900">Dashboard</h1>
                  <p className="text-sm text-slate-500">Welcome back, {getDisplayNameForUI(user) || 'Admin'}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 md:gap-4">
                <div className="hidden md:flex items-center gap-2">
                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    className="dashboard-select"
                  >
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="90d">Last 90 days</option>
                  </select>
                </div>
                <div className="hidden md:flex items-center gap-2">
                  <AdminLocationDropdown
                    value={selectedLocation}
                    onChange={(v) => {
                      setSelectedLocation(v);
                      try {
                        if (!v) {
                          localStorage.removeItem('adminLocation');
                        } else if (typeof v === 'object') {
                          localStorage.setItem('adminLocation', JSON.stringify(v));
                        } else {
                          localStorage.setItem('adminLocation', String(v));
                        }
                      } catch (e) {}
                    }}
                  />
                </div>
                <button type="button" className="relative p-2.5 text-slate-600 hover:text-primary-600 hover:bg-slate-100 rounded-xl transition" aria-label="Notifications">
                  <FaBell className="text-xl" />
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-primary-500 rounded-full ring-2 ring-white" />
                </button>

                {/* Profile Dropdown */}
                <div className="relative profile-menu">
                  <button
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <img loading="lazy"
                      src={user?.avatar || AVATAR_PLACEHOLDER}
                      alt="Profile"
                      className="w-8 h-8 rounded-full border-2 border-gray-300"
                    />
                    <span className="hidden md:inline text-sm font-medium text-gray-700">{getDisplayNameForUI(user) || '—'}</span>
                    <FaChevronDown className="text-xs text-gray-500" />
                  </button>

                  {showProfileMenu && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-slate-200 z-50 overflow-hidden">
                      <div className="p-4 border-b border-slate-100 bg-slate-50/50">
                        <div className="flex items-center gap-3">
                          <img loading="lazy"
                            src={user?.avatar || AVATAR_PLACEHOLDER}
                            alt="Profile"
                            className="w-10 h-10 rounded-full border-2 border-gray-300"
                          />
                          <div>
                            <p className="font-semibold text-gray-900 text-sm">{getDisplayNameForUI(user) || '—'}</p>
                            <p className="text-xs text-gray-600 capitalize">{getDisplayRole(user)}</p>
                          </div>
                        </div>
                      </div>
                      <div className="p-2">
                        <button
                          onClick={() => {
                            navigate('/admin/dashboard/profile');
                            setShowProfileMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors text-sm"
                        >
                          <FaUser className="text-sm" />
                          <span>My Profile</span>
                        </button>
                        <button
                          onClick={() => {
                            navigate('/login');
                            setShowProfileMenu(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors text-sm mt-1"
                        >
                          <FaSignInAlt className="text-sm" />
                          <span>Login</span>
                        </button>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors text-sm mt-1"
                        >
                          <FaSignOutAlt className="text-sm" />
                          <span>Logout</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="dashboard-main">
          <div className="p-4 md:p-6 lg:p-8">
            {currentPage === 'overview' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
                {loading && stats.length === 0 && (
                  <div className="col-span-full flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-200 border-t-primary-600" />
                  </div>
                )}
                {stats.map((stat, index) => {
                  const Icon = stat.icon;
                  const trend = stat.trend || 'neutral';
                  const showVsPeriod = trend !== 'neutral' && typeof stat.change === 'string' && stat.change.includes('%');
                  const TrendIcon = trend === 'up' ? FaArrowUp : trend === 'down' ? FaArrowDown : null;
                  return (
                    <div
                      key={index}
                      onClick={() => navigate(stat.path)}
                      className="stat-card-clickable p-5"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="stat-card-label">{stat.label}</p>
                          <p className="stat-card-value mt-1">{stat.value}</p>
                          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                            {TrendIcon && trend !== 'neutral' && (
                              <TrendIcon className={`text-xs shrink-0 ${trend === 'up' ? 'text-emerald-500' : 'text-red-500'}`} />
                            )}
                            <span className={`stat-card-change ${trend}`}>
                              {stat.change}
                            </span>
                            {showVsPeriod && (
                              <span className="text-xs text-slate-400">vs previous period</span>
                            )}
                          </div>
                        </div>
                        <div className={`stat-card-icon-wrap ${stat.color}`}>
                          <Icon className="text-xl" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className={`grid grid-cols-1 ${currentPage === 'overview' ? 'lg:grid-cols-3' : ''} gap-6`}>
              <div className={currentPage === 'overview' ? 'lg:col-span-2 space-y-6' : 'space-y-6'}>
                <div className="dashboard-card p-0">
                  <div className="p-6">
                  {currentPage === 'overview' && (
                    <Overview
                      timeSeriesData={timeSeriesData}
                      dateRange={dateRange}
                      stats={stats}
                      topVendors={topVendorsData}
                      recentActivities={recentActivities}
                    />
                  )}
                  {currentPage === 'users' && (
                    <Users />
                  )}
                  {currentPage === 'orders' && (
                    <Orders ordersData={orders} />
                  )}
                  {currentPage === 'availability' && (
                    <AvailabilityRequestsPanel />
                  )}
                  {currentPage === 'vendors' && (
                    <Vendors topVendors={topVendorsData} />
                  )}
                  {currentPage === 'garages' && (
                    <Garages />
                  )}
                  {currentPage === 'vehicles' && (
                    <VehicleManagement />
                  )}
                  {currentPage === 'catalog-gaps' && (
                    <CatalogGapsPanel />
                  )}
                  {currentPage === 'seller-review-queue' && (
                    <SellerReviewQueuePanel />
                  )}
                  {currentPage === 'catalog-approvals' && (
                    <CatalogApprovalsPanel />
                  )}
                  {currentPage === 'jobcards-qc' && (
                    <JobCardsQC />
                  )}
                  {currentPage === 'mechanics' && (
                    <Mechanics />
                  )}
                  {currentPage === 'shipping' && (
                    <Shipping />
                  )}
                  {currentPage === 'products' && (
                    <Products />
                  )}
                  {currentPage === 'categories' && (
                    <Categories />
                  )}
                  {currentPage === 'lucky-draw' && (
                    <LuckyDraw />
                  )}
                  {currentPage === 'kyc' && (
                    <KYC />
                  )}
                  {currentPage === 'partner-applications' && (
                    <PartnerApplicationsPanel />
                  )}
                  {currentPage === 'fines' && (
                    <Fines />
                  )}
                  {currentPage === 'analytics' && (
                    <Analytics timeSeriesData={timeSeriesData} stats={stats} />
                  )}
                  {currentPage === 'reports' && (
                    <Reports />
                  )}
                  {currentPage === 'settings' && (
                    <Settings />
                  )}
                  {currentPage === 'profile' && (
                    <Profile />
                  )}
                  {currentPage === 'payments' && (
                    <Payments />
                  )}
                  {currentPage === 'invoices' && (
                    <Invoices />
                  )}
                  {currentPage === 'marketing' && (
                    <Marketing />
                  )}
                  {currentPage === 'deals' && (
                    <Deals />
                  )}
                  {currentPage === 'deal-audit' && (
                    <DealAuditLogs />
                  )}
                  {currentPage === 'combo-failures' && (
                    <ComboFailureDashboard />
                  )}
                  {currentPage === 'support' && (
                    <Support />
                  )}
                  {currentPage === 'fraud' && (
                    <FraudDetection />
                  )}
                  {currentPage === 'risk' && (
                    <RiskDashboard />
                  )}
                  {currentPage === 'disputes' && (
                    <DisputeArbitrationPanel />
                  )}
                  {currentPage === 'comms-rules' && (
                    <CommsRules />
                  )}
                  {currentPage === 'commissions' && (
                    <Commissions />
                  )}
                </div>
              </div>

              {/* Right Sidebar - Only visible on Overview page */}
              {currentPage === 'overview' && (
                <div className="space-y-6">
                  <div className="dashboard-card">
                    <div className="dashboard-card-header flex items-center justify-between">
                      <h3 className="dashboard-card-title">Recent activity</h3>
                      <button type="button" className="text-sm font-medium text-primary-600 hover:text-primary-700">View all</button>
                    </div>
                    <div className="p-4 space-y-3">
                      {recentActivities.length === 0 ? (
                        <p className="text-center py-6 text-slate-500 text-sm">No recent activities</p>
                      ) : (
                        recentActivities.map((activity) => {
                          const Icon = getActivityIcon(activity.type);
                          return (
                            <div key={activity.id} className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0 last:pb-0">
                              <div className={`p-2 rounded-xl shrink-0 ${activity.type === 'user' ? 'bg-sky-100 text-sky-600' : activity.type === 'order' ? 'bg-emerald-100 text-emerald-600' : activity.type === 'product' ? 'bg-violet-100 text-violet-600' : 'bg-slate-100 text-slate-600'}`}>
                                <Icon className="text-sm" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm text-slate-800">{activity.message}</p>
                                <p className="text-xs text-slate-500 mt-0.5">{activity.time}</p>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <div className="dashboard-card">
                    <div className="dashboard-card-header">
                      <h3 className="dashboard-card-title">Quick actions</h3>
                    </div>
                    <div className="p-4 space-y-2">
                      {[
                        { label: 'Manage Users', icon: FaUserShield, path: '/admin/dashboard/users', color: 'hover:bg-sky-50 text-sky-700' },
                        { label: 'Manage Vendors', icon: FaStore, path: '/admin/dashboard/vendors', color: 'hover:bg-emerald-50 text-emerald-700' },
                        { label: 'Manage Mechanics', icon: FaTools, path: '/admin/dashboard/mechanics', color: 'hover:bg-amber-50 text-amber-700' },
                        { label: 'Manage Shipping', icon: FaTruck, path: '/admin/dashboard/shipping', color: 'hover:bg-sky-50 text-sky-700' },
                        { label: 'View Analytics', icon: FaChartLine, path: '/admin/dashboard/analytics', color: 'hover:bg-violet-50 text-violet-700' },
                        { label: 'System Settings', icon: FaCog, path: '/admin/dashboard/settings', color: 'hover:bg-slate-100 text-slate-700' },
                      ].map(({ label, icon: Icon, path, color }) => (
                        <button key={path} type="button" onClick={() => navigate(path)} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-left text-sm font-medium border border-slate-200 bg-white ${color} transition`}>
                          <Icon className="text-lg shrink-0" />
                          <span>{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="dashboard-card">
                    <div className="dashboard-card-header">
                      <h3 className="dashboard-card-title">System status</h3>
                    </div>
                    <div className="p-4 space-y-3">
                      {[
                        { label: 'API', value: systemStatus.apiStatus || 'Unknown', ok: systemStatus.apiStatus === 'Operational' },
                        { label: 'Database', value: systemStatus.database || 'Unknown', ok: systemStatus.database === 'Healthy' },
                        { label: 'Server load', value: `${systemStatus.serverLoad ?? 0}%`, ok: (systemStatus.serverLoad ?? 0) < 80 },
                        { label: 'Uptime', value: systemStatus.uptime || '0%', ok: true },
                      ].map(({ label, value, ok }) => (
                        <div key={label} className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">{label}</span>
                          <span className="flex items-center gap-2">
                            {label !== 'Uptime' && label !== 'Server load' && (
                              <span className={`w-2 h-2 rounded-full ${ok ? 'bg-emerald-500' : 'bg-red-500'}`} />
                            )}
                            <span className={`text-sm font-semibold ${ok ? 'text-slate-900' : 'text-red-600'}`}>{value}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        </main>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;

